import React, { Component } from 'react'
import T from 'prop-types'
import {
  FormHelperText,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
} from '@material-ui/core'
import DropdownIcon from '@material-ui/icons/ArrowDropDown'
import { Map, List } from 'immutable'
import isEqual from 'lodash/isEqual'
import cn from 'classnames'
import { formatMessage } from 'lib/intl'
import FuncHelper from 'helpers/functions'
import MenuItem from 'components/override/MaterialUI/MenuItem'
import { JOBS_DATA_VIEWER_DEFAULT_FIELDS } from 'constants/index'

const EMPTY_VALUE = '__EMPTY__'
const ALL_VALUE = '__ALL__'
const getActiveValue = value => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE
  }

  return value
}

/**
 * @property list - list options data (type Immutable.List);
 * @property map - the object, which show how parse list data;
 * @property value - main value param for selectList;
 * @property outMultipleValues - свойства empty и all служат для подмены выходных значений когда у нас в списке либо выбрано все, либо не выбрано ничего. Актуально только для ванианта multiple;
 */
class SelectList extends Component {
  state = {}

  constructor(props) {
    super(props)

    const { list: propList, map: propMap, value } = this.props
    const { list, valueList } = this.map(propList, propMap)
    const activeValue = this.checkMultipleValue(value, null, list)

    this.state = { list, valueList, activeValue }
  }

  componentDidUpdate(prevProps) {
    const { value: prevValue, list: prevList } = prevProps
    const {
      value,
      list,
      map,
      multiple,
      hasAllValueOption,
      setFirstValue,
    } = this.props

    const isNotEqualList = prevList !== list
    const isNotEqualValue = !isEqual(value, prevValue)

    if (isNotEqualList || isNotEqualValue) {
      let newState = {}
      let setValueFunc = () => {}

      if (isNotEqualList) {
        const mapped = this.map(list, map)
        newState = { ...newState, ...mapped }

        if (!multiple && setFirstValue) {
          setValueFunc = () =>
            this.setActiveValue(this.state.list[0].index, true)
        }

        if (multiple && hasAllValueOption) {
          setValueFunc = () => this.setActiveValue(value, true)
        }
      }

      if (isNotEqualValue) {
        setValueFunc = () => this.setActiveValue(value, true)
      }

      if (Object.keys(newState).length > 0) {
        this.setState(newState, setValueFunc)
      } else {
        setValueFunc()
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Simplify this method to prevent unnecessary re-renders
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState)
  }

  map = (itemArr, itemObj) => {
    if (itemArr instanceof Map) {
      // eslint-disable-next-line no-param-reassign
      itemArr = itemArr.toJS()
    } else if (itemArr instanceof List) {
      // eslint-disable-next-line no-param-reassign
      itemArr = itemArr.toArray()
    } else {
      throw new Error('Incorrect list format. Only Immutable Map or List')
    }

    const defIndex = 'INDEX'
    const defText = 'ITEM'
    const list = []
    const valueList = {}
    let i = defIndex
    let t = defText
    let counter = 0

    if (typeof itemObj === 'object') {
      const { index, text } = itemObj
      if (index !== undefined && index !== null) i = index
      if (text !== undefined && text !== null) t = text
    }

    for (const key in itemArr) {
      if (itemArr.hasOwnProperty(key) && Boolean(itemArr[key])) {
        const item = {
          index:
            // eslint-disable-next-line no-nested-ternary
            i === defIndex
              ? key
              : typeof i === 'function'
              ? i(itemArr[key], key)
              : itemArr[key][i],
          text:
            // eslint-disable-next-line no-nested-ternary
            t === defText
              ? itemArr[key]
              : typeof t === 'function'
              ? t(itemArr[key], key)
              : itemArr[key][t],
          item: itemArr[key],
          dataKey: key,
          mapKey: counter,
        }

        list.push(item)
        valueList[item.index] = item.mapKey
        counter += 1
      }
    }

    return { list, valueList }
  }

  get value() {
    const { list, valueList, activeValue } = this.state
    const {
      multiple,
      hasAllValueOption,
      outMultipleValues: { all, empty },
    } = this.props

    if (!multiple) {
      return list[valueList[activeValue]]
    }

    const values = hasAllValueOption
      ? activeValue.filter(item => item !== ALL_VALUE)
      : activeValue

    if (all !== undefined && values.length === list.length) {
      return all
    }

    if (empty !== undefined && !values.length) {
      return empty
    }

    return values
      .map(item => list[valueList[item]])
      .filter(item => item !== undefined)
  }

  checkMultipleValue = (currentValue, prevActiveValue, listData) => {
    let nextValue = getActiveValue(currentValue)
    const { multiple, hasAllValueOption } = this.props

    if (multiple && hasAllValueOption) {
      const prevValue = prevActiveValue || []
      const prevAllValues = prevValue.includes(ALL_VALUE)
      const currentAllValues = nextValue.includes(ALL_VALUE)

      if (!prevAllValues && currentAllValues) {
        nextValue = [ALL_VALUE].concat(listData.map(item => item.index))
      }

      if (prevAllValues && !currentAllValues) {
        nextValue = []
      }

      if (
        prevAllValues &&
        currentAllValues &&
        nextValue.length <= listData.length
      ) {
        nextValue = nextValue.filter(item => item !== ALL_VALUE)
      }

      if (
        !prevAllValues &&
        !currentAllValues &&
        nextValue.length === listData.length
      ) {
        nextValue = [ALL_VALUE].concat(nextValue)
      }
    }

    return nextValue
  }

  setActiveValue = (value, isInitValue) => {
    const { activeValue: prevValue, list } = this.state
    const previousValue = isInitValue ? null : prevValue
    const activeValue = this.checkMultipleValue(value, previousValue, list)

    this.setState({ activeValue }, () => {
      const { onChange } = this.props

      if (typeof onChange === 'function') {
        onChange(this.value)
      }
    })
  }

  onChange = e => {
    if (e.target.value !== this.state.activeValue) {
      this.setActiveValue(e.target.value)
    }
  }

  onClose = () => {
    const { onClose } = this.props

    if (typeof onClose === 'function') {
      onClose(this.value)
    }
  }

  getLoaderIcon = () => {
    const { loader } = this.props

    return (
      <span className="select-loader">
        {loader ? <CircularProgress size={20} /> : <DropdownIcon />}
      </span>
    )
  }

  renderValue = value => {
    const { valueList, list } = this.state
    let inputValues = value.includes(ALL_VALUE)
      ? formatMessage('all_selected')
      : ''

    if (!inputValues && list.length) {
      inputValues = value
        .filter(key => list.hasOwnProperty(valueList[key]))
        .map(key => list[valueList[key]].text)
        .join(', ')
    }

    return inputValues
  }

  render() {
    const {
      label,
      name,
      disabled,
      className,
      multiple,
      required,
      controlError,
      hasAllValueOption,
      withCheckBox,
      native,
    } = this.props
    const { list, activeValue } = this.state
    const withoutCheckIcon = !withCheckBox
    const renderChildrenItem = native
      ? (item, key) => (
          <option key={key} value={item.index}>
            {item.text}
          </option>
        )
      : (menuItem, key) => {
          const { item: { enabled = true } = {}, text, index } = menuItem
          const isDefaultSelected =
            JOBS_DATA_VIEWER_DEFAULT_FIELDS.indexOf(menuItem.index) >= 0

          return (
            <MenuItem
              key={key}
              value={index}
              withoutCheckIcon={withoutCheckIcon}
              className="multiselect-item"
              defaultSelected={isDefaultSelected}
              disabled={isDefaultSelected || !enabled}>
              {text}
            </MenuItem>
          )
        }

    return (
      <FormControl
        fullWidth
        component="div"
        required={required}
        error={!!controlError}
        className={cn('select-filter', className)}>
        <InputLabel htmlFor={name}>{label}</InputLabel>
        <Select
          id={name}
          name={name}
          value={activeValue}
          disabled={disabled || !list.length}
          multiple={multiple}
          onChange={this.onChange}
          onClose={this.onClose}
          IconComponent={this.getLoaderIcon}
          renderValue={multiple ? this.renderValue : undefined}
          native={native}
          MenuProps={{
            className: cn({
              'multiselect-menu': multiple,
              'all-selected-mode': hasAllValueOption,
            }),
            getContentAnchorEl: null,
          }}>
          {Boolean(multiple && hasAllValueOption) &&
            renderChildrenItem(
              {
                index: ALL_VALUE,
                text: formatMessage('all_selected'),
              },
              ALL_VALUE,
            )}
          {list.map(item => renderChildrenItem(item, `item-${item.index}`))}
        </Select>
        {controlError && <FormHelperText>{controlError}</FormHelperText>}
      </FormControl>
    )
  }
}

SelectList.defaultProps = {
  list: [],
  onChange: FuncHelper.emptyFunc,
  outMultipleValues: {
    all: undefined,
    empty: undefined,
  },
}

SelectList.propTypes = {
  list: T.oneOfType([T.array, T.object]),
  onChange: T.func,
  setFirstValue: T.bool,
  outMultipleValues: T.shape({
    all: T.any,
    empty: T.any,
  }),
}

export default SelectList
